import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Text } from 'styles';

export function ScrollAside({ setRef, list }) {
  const [currentNav, setCurrentNav] = useState();
  const [activeKey, setActiveKey] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  const moveScroll = (item, index) => {
    if (location.pathname.includes('community')) {
      navigate(item.path);
    } else if (location.pathname !== item.path) {
      // 다른 페이지에서 클릭한 경우 해당 페이지로 이동하고 인덱스 저장
      sessionStorage.setItem('currentIndex', String(index));
      navigate(item.path);
    } else {
      // 같은 페이지에서 클릭한 경우 스크롤만 이동
      setRef.current[index].scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    }
  };

  useEffect(() => {
    if (list && Array.isArray(list)) {
      const pathSegment = location.pathname.split('/')[1] || '';
      const foundNav = list.find((el) => el.path && el.path.includes(pathSegment));
      setCurrentNav(foundNav);
    }
  }, [list, location.pathname]);

  useEffect(() => {
    if (sessionStorage.getItem('currentIndex')) {
      setActiveKey(Number(sessionStorage.getItem('currentIndex')));
    }
  }, [sessionStorage.getItem('currentIndex')]);

  // 헤더 메뉴로 같은 페이지 내 이동한 경우
  useEffect(() => {
    const onChange = () => setActiveKey(Number(sessionStorage.getItem('currentIndex')));
    window.addEventListener('currentIndexChange', onChange);
    return () => window.removeEventListener('currentIndexChange', onChange);
  }, []);

  if (!currentNav || !currentNav.sub || !Array.isArray(currentNav.sub)) {
    return null;
  }

  return (
    <Container>
      <ul>
        {currentNav.sub.map((item, index) => {
          if (item.group) {
            // 그룹 헤더 (제품설치 | 설치 장소)
            return (
              <GroupHeader key={`group_${item.group}_${index}`}>
                <Text
                  $type="b5"
                  $tType="b7"
                  $tLineHeight="1"
                  $textAlign="center"
                  $color="secondary"
                  $fontWeight={500}
                >
                  {item.name}
                </Text>
              </GroupHeader>
            );
          } else if (item.parent) {
            // 그룹 하위 항목 - index 사용
            const navIndex = item.index !== undefined ? item.index : index;
            
            return (
              <AsideListItem
                key={`item_${index}`}
                $active={activeKey === navIndex}
                $isSubItem
                onClick={() => {
                  setActiveKey(navIndex);
                  moveScroll(item, navIndex);
                }}
              >
                <Text
                  $color={activeKey === navIndex && 'primary'}
                  $fontWeight={(activeKey === navIndex && 700) || 500}
                  $type="b5"
                  $tType="b7"
                  $tLineHeight="1"
                  $textAlign="center"
                >
                  {item.name}
                </Text>
              </AsideListItem>
            );
          } else {
            // 일반 항목 - index 사용
            const navIndex = item.index !== undefined ? item.index : index;
            
            return (
              <AsideListItem
                key={`item_${index}`}
                $active={activeKey === navIndex}
                onClick={() => {
                  setActiveKey(navIndex);
                  moveScroll(item, navIndex);
                }}
              >
                <Text
                  $color={activeKey === navIndex && 'primary'}
                  $fontWeight={(activeKey === navIndex && 700) || 500}
                  $type="b5"
                  $tType="b7"
                  $tLineHeight="1"
                  $textAlign="center"
                >
                  {item.name}
                </Text>
              </AsideListItem>
            );
          }
        })}
      </ul>
    </Container>
  );
}

const Container = styled.aside`
  position: sticky;
  align-self: flex-start;
  z-index: 5;
  top: 208px;
  border-radius: 16px 0 16px 0;
  box-shadow: ${({ theme }) => theme.colors.boxShadow};
  background-color: white;

  & > ul > li {
    cursor: pointer;
    padding: 14px 24px;
    min-width: 272px;
    width: 100%;
  }

  & > ul > li:nth-last-child(1) {
    border-bottom: 0;
  }

  @media ${({ theme }) => theme.device.tablet} {
    display: flex;
    width: 100%;
    top: 104px;
    box-shadow: none;
    border-radius: unset;

    & > ul {
      display: flex;
      width: 100%;
    }

    & > ul > li {
      padding: 22px 0;
      min-width: unset;
    }

    & > ul > li:nth-last-child(1) {
      border-bottom: 1px solid ${({ theme }) => theme.colors.fill03};
    }
  }
`;

const AsideListItem = styled.li`
  position: relative;
  overflow: hidden;
  padding-left: ${({ $isSubItem }) => ($isSubItem ? '32px' : '0')};

  &:last-of-type {
    border-radius: 0 0 16px 0;
  }

  &::before {
    content: '';
    position: absolute;
    z-index: 1;
    width: 0;
    left: 0;
    bottom: 0;
    height: 2px;
    transition: all linear 250ms;
    background-color: ${({ theme }) => theme.colors.primary};
  }

  &::after {
    content: '';
    position: absolute;
    width: 100%;
    left: 0;
    bottom: 0;
    height: 2px;
    background-color: ${({ $active, theme }) => ($active && `${theme.colors.primary}`) || `${theme.colors.fill03}`};
  }

  &:hover {
    &::before {
      width: 100%;
    }
  }
`;

const GroupHeader = styled.li`
  padding: 14px 24px;
  background-color: ${({ theme }) => theme.colors.fill02};
  border-bottom: 1px solid ${({ theme }) => theme.colors.fill03};
  cursor: default;
`;
